import { analytics } from '~/_plugins/analytics/core'
import { useAccountStore } from '~/stores/account'
import type {
  BracketMatch,
  PickemTeam,
  RewardLevel,
  Stage,
  StageSummary,
  StageUnlock,
  SwissCategory,
  Tournament,
  TournamentUnion,
  UnlockCondition
} from '../types'

export const PICKEM_SCREEN_NAME = 'ligapass_pickem'

export type PickemStageSourceBlock = 'sidebar' | 'stage_carousel'
export type PickemPickAction = 'add' | 'replace' | 'remove'

export interface PickemPickAnalyticsMeta {
  pick_action: PickemPickAction
  pick_outcome_group: string
  pick_slot_index: number
  team: PickemTeam
  stage: Stage
  picks_count_after: number
}

const tournamentStatusMap: Record<Tournament['status'], string> = {
  draft: 'upcoming',
  active: 'active',
  finished: 'finished'
}

export const mapStageType = (type: StageSummary['type']): 'cell' | 'grid' =>
  type === 'swiss' ? 'cell' : 'grid'

export const mapSwissOutcomeGroup = (category: SwissCategory): string => {
  const isCleanWin =
    category.wins.length === 1 &&
    category.wins[0] === 3 &&
    category.losses.includes(0)
  const isCleanLoss =
    category.losses.length === 1 &&
    category.losses[0] === 3 &&
    category.wins.includes(0)

  if (isCleanWin) return '3-0'
  if (isCleanLoss) return '0-3'
  return '3-1_3-2'
}

const mapUnlockLogic = (
  combineMode: StageUnlock['combine_mode']
): 'any' | 'all' => (combineMode === 'OR' ? 'any' : 'all')

const countUnlockConditionsMet = (unlock: StageUnlock): number =>
  unlock.conditions.filter(condition => condition.user_satisfied).length

const mapUnlockAmount = (condition: UnlockCondition | undefined): number => {
  if (!condition) return 0
  const amount = condition.params.min_amount
  return typeof amount === 'number' ? amount / 100 : 0
}

const mapRewardType = (slug: string): string => {
  const normalized = slug.toLowerCase()
  if (normalized.includes('freebet') || normalized.includes('free-bet')) {
    return 'freebet'
  }
  if (normalized.includes('coin')) return 'coins'
  return normalized
}

const getConditionBetType = (betType: number | null) => {
  if (betType === 1) {
    return 'single'
  }

  if (betType === 2) {
    return 'express'
  }

  return null
}

export const buildRewardAnalyticsPayload = (level: RewardLevel) => {
  const types: string[] = []

  for (const item of level.rewards) {
    types.push(mapRewardType(item.resource.slug))
  }
  if (level.experience?.points) {
    types.push('xp')
  }

  const freebetItem = level.rewards.find(
    item => mapRewardType(item.resource.slug) === 'freebet'
  )
  const coinsItem = level.rewards.find(
    item => mapRewardType(item.resource.slug) === 'coins'
  )

  return {
    reward_position: level.position,
    reward_points: level.points_required,
    reward_types: types,
    reward_freebet_amount: freebetItem?.amount ?? null,
    reward_xp_amount: level.experience?.points ?? null,
    reward_coins_amount: coinsItem?.amount ?? null
  }
}

const basePayload = () => {
  const accountStore = useAccountStore()

  return {
    screen_name: PICKEM_SCREEN_NAME,
    authorization_flag: accountStore.isAuthorized
  }
}

export const getSourceScreenName = (): string => {
  const { history } = useRouter().options

  if ('back' in history.state && typeof history.state.back === 'string') {
    return history.state.back
  }

  return 'link'
}

export const buildTournamentPayload = (tournament: TournamentUnion) => ({
  tournament_id: tournament.id,
  tournament_name: tournament.name,
  tournament_game: tournament.game.code,
  tournament_status:
    tournamentStatusMap['status' in tournament ? tournament.status : 'finished']
})

export const buildStageSummaryPayload = (stage: StageSummary) => ({
  stage_id: stage.id,
  stage_name: stage.name,
  stage_position: stage.position,
  stage_status: stage.ui_state,
  stage_type: mapStageType(stage.type)
})

export const buildStageFullPayload = (stage: Stage) => ({
  stage_id: stage.id,
  stage_name: stage.name,
  stage_position: stage.position,
  stage_status: stage.ui_state,
  stage_type: mapStageType(stage.type)
})

export const buildPickPayload = (
  meta: PickemPickAnalyticsMeta,
  tournament: Tournament
) => ({
  ...basePayload(),
  ...buildStageFullPayload(meta.stage),
  ...buildTournamentPayload(tournament),
  pick_action: meta.pick_action,
  pick_outcome_group: meta.pick_outcome_group,
  pick_slot_index: meta.pick_slot_index,
  team_id: meta.team.id,
  team_name: meta.team.name,
  team_tag: meta.team.tag,
  picks_count_after: meta.picks_count_after,
  picks_max: meta.stage.slots_total
})

export const usePickemAnalytics = () => {
  const trackScreenView = (payload: {
    tournament: Tournament
    stage: StageSummary
    userProgressPoints: number
    userProgressMax: number
    sourceScreenName?: string
  }) => {
    analytics('LigaPass_Pickem_ScreenView', {
      ...basePayload(),
      ...buildTournamentPayload(payload.tournament),
      ...buildStageSummaryPayload(payload.stage),
      source_screen_name: payload.sourceScreenName ?? getSourceScreenName(),
      user_progress_points: payload.userProgressPoints,
      user_progress_max: payload.userProgressMax,
      picks_count: payload.stage.slots_filled,
      picks_max: payload.stage.slots_total
    })
  }

  const trackTournamentLineClick = (payload: {
    tournament: Tournament
    tournamentUrl: string
  }) => {
    analytics('LigaPass_PickemTournamentLine_ButtonClick', {
      ...basePayload(),
      ...buildTournamentPayload(payload.tournament),
      tournament_url: payload.tournamentUrl
    })
  }

  const trackMatchLineClick = (payload: {
    match: BracketMatch
    stage: Stage
    tournament: Tournament
  }) => {
    const left = payload.match.slots.find(slot => slot.side === 'left')
    const right = payload.match.slots.find(slot => slot.side === 'right')
    const now = Date.now()
    const startsAtMs = payload.match.starts_at
      ? payload.match.starts_at * 1000
      : null

    analytics('LigaPass_PickemMatchLine_ButtonClick', {
      ...basePayload(),
      ...buildTournamentPayload(payload.tournament),
      ...buildStageFullPayload(payload.stage),
      match_round: payload.match.column_code,
      match_pair_index: payload.match.position,
      match_datetime: startsAtMs
        ? new Date(startsAtMs).toISOString().slice(0, 19)
        : null,
      sport_event_id: payload.match.id,
      sport_event_name: `${left?.team?.name ?? '—'} — ${right?.team?.name ?? '—'}`,
      sport_name: payload.tournament.game.name,
      match_url: payload.match.line_url,
      event_type:
        startsAtMs && startsAtMs <= now && !payload.match.played_at
          ? 'live'
          : 'prematch',
      team_left_id: left?.team?.id ?? null,
      team_left_name: left?.team?.name ?? null,
      team_right_id: right?.team?.id ?? null,
      team_right_name: right?.team?.name ?? null
    })
  }

  const trackLeaderboardClick = (tournament: TournamentUnion) => {
    analytics('LigaPass_PickemLeaderboard_ButtonClick', {
      ...basePayload(),
      ...buildTournamentPayload(tournament)
    })
  }

  const trackLeaderboardScreenView = (tournament: Tournament) => {
    analytics('LigaPass_PickemLeaderboard_ScreenView', {
      ...basePayload(),
      ...buildTournamentPayload(tournament)
    })
  }

  const trackStageClick = (payload: {
    stage: StageSummary
    tournament: Tournament
    sourceBlock: PickemStageSourceBlock
    unlock?: StageUnlock | null
  }) => {
    const firstCondition = payload.unlock?.conditions[0]

    analytics('LigaPass_PickemStage_ButtonClick', {
      ...basePayload(),
      ...buildTournamentPayload(payload.tournament),
      ...buildStageSummaryPayload(payload.stage),
      source_block: payload.sourceBlock
    })
  }

  const trackSwitch = (payload: {
    tabValue: 'pick' | 'result'
    stage: Stage
    tournament: Tournament
  }) => {
    analytics('LigaPass_Pickem_Switch', {
      ...basePayload(),
      ...buildStageFullPayload(payload.stage),
      ...buildTournamentPayload(payload.tournament),
      tab_value: payload.tabValue === 'pick' ? 'pickem' : 'result'
    })
  }

  const trackPickClick = (
    meta: PickemPickAnalyticsMeta,
    tournament: Tournament
  ) => {
    analytics(
      'LigaPass_PickemPick_ButtonClick',
      buildPickPayload(meta, tournament)
    )
  }

  const trackPickSuccess = (
    meta: PickemPickAnalyticsMeta,
    tournament: Tournament
  ) => {
    analytics('LigaPass_PickemPick_Success', buildPickPayload(meta, tournament))
  }

  const trackUnlockScreenView = (payload: {
    stage: Stage
    tournament: Tournament
    sourceBlock: PickemStageSourceBlock
  }) => {
    analytics('LigaPass_PickemUnlock_ScreenView', {
      ...basePayload(),
      ...buildTournamentPayload(payload.tournament),
      stage_id: payload.stage.id,
      stage_name: payload.stage.name,
      stage_position: payload.stage.position,
      stage_type: mapStageType(payload.stage.type),
      stage_unlock_logic: mapUnlockLogic(payload.stage.unlock.combine_mode),
      conditions_total: payload.stage.unlock.conditions.length,
      conditions_met: countUnlockConditionsMet(payload.stage.unlock),
      source_block: payload.sourceBlock
    })
  }

  const trackUnlockClick = (payload: {
    stage: Stage
    unlock: StageUnlock
    condition: UnlockCondition
    conditionIndex: number
    tournament: Tournament
  }) => {
    analytics('LigaPass_PickemUnlock_ButtonClick', {
      ...basePayload(),
      ...buildTournamentPayload(payload.tournament),
      condition_index: payload.conditionIndex,
      condition_type: payload.condition.type,
      condition_amount: mapUnlockAmount(payload.condition),
      condition_bet_type: getConditionBetType(
        'bet_type_id' in payload.condition.params
          ? (payload.condition.params.bet_type_id as number)
          : null
      ),
      condition_coefficient: payload.condition.params.min_total_odd as number,
      condition_url: payload.condition.action_url,
      stage_id: payload.stage.id,
      stage_name: payload.stage.name,
      stage_unlock_logic: mapUnlockLogic(payload.unlock.combine_mode),
      conditions_total: payload.unlock.conditions.length,
      conditions_met: countUnlockConditionsMet(payload.unlock)
    })
  }

  const trackRewardClick = (payload: {
    level: RewardLevel
    tournament: Tournament
  }) => {
    analytics('LigaPass_PickemReward_ButtonClick', {
      ...basePayload(),
      ...buildRewardAnalyticsPayload(payload.level),
      ...buildTournamentPayload(payload.tournament)
    })
  }

  const trackGuideClick = (payload: { tournament: Tournament }) => {
    analytics('LigaPass_PickemGuide_ButtonClick', {
      ...basePayload(),
      ...buildTournamentPayload(payload.tournament)
    })
  }

  const trackTournamentListScreenView = () => {
    analytics('LigaPass_PickemAllTournaments_ScreenView', {
      ...basePayload()
    })
  }

  const trackTournamentListCardClick = (tournament: TournamentUnion) => {
    analytics('LigaPass_PickemAllTournaments_ButtonClick', {
      ...basePayload(),
      ...buildTournamentPayload(tournament)
    })
  }

  return {
    trackScreenView,
    trackTournamentLineClick,
    trackMatchLineClick,
    trackLeaderboardClick,
    trackLeaderboardScreenView,
    trackStageClick,
    trackSwitch,
    trackPickClick,
    trackPickSuccess,
    trackUnlockScreenView,
    trackUnlockClick,
    trackRewardClick,
    trackGuideClick,
    trackTournamentListScreenView,
    trackTournamentListCardClick
  }
}
